import type { GenericSpecification, TabSection } from './eto-app';
import CustomSelect from './custom-select';

const displayText = (value: unknown): string => {
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number') {
        return String(value);
    }
    if (Array.isArray(value)) {
        const entries = value.filter((entry) => entry && typeof entry === 'object') as { isocode?: string; value?: unknown }[];
        const preferred = entries.find((entry) => String(entry.isocode || '').toLowerCase().startsWith('en'))
            || entries.find((entry) => typeof entry.value === 'string');
        return typeof preferred?.value === 'string' ? preferred.value : '';
    }
    if (value && typeof value === 'object' && typeof (value as { value?: unknown }).value === 'string') {
        return (value as { value: string }).value;
    }
    return '';
};

const specRows = (specifications: GenericSpecification[]): GenericSpecification[] => specifications.flatMap((specification) => {
    const rawValue = specification.value as unknown;
    if (Array.isArray(rawValue) && rawValue.length > 0 && rawValue.every((entry) => entry && typeof entry === 'object' && !('isocode' in (entry as object)))) {
        return rawValue.map((entry) => ({
            ...specification,
            name: displayText((entry as { name?: unknown }).name),
            value: displayText((entry as { value?: unknown }).value),
        }));
    }
    return [{
        ...specification,
        name: displayText(specification.name),
        value: displayText(specification.value),
    }];
}).filter((specification) => specification.name || specification.value);

function GenericSpecificationSection({ componentName, componentsLoading, error, specifications, sectionTabs, activeTabName, activeSubtabName, setActiveSubsection, updateUrlHash, i18n }: {
    componentName: string;
    componentsLoading: boolean;
    error: string | null;
    specifications: GenericSpecification[];
    sectionTabs: TabSection[];
    activeTabName: string;
    activeSubtabName: string | undefined;
    setActiveSubsection: (tabName: string, subtabName: string | null, subsectionName: string) => void;
    updateUrlHash: () => void;
    i18n: (key: string) => string;
}) {
    const rows = specRows(specifications);
    const otherSectionName = i18n('Other');
    const sections: { name: string; items: GenericSpecification[]; subsections: { name: string; items: GenericSpecification[] }[] }[] = [];
    rows.forEach((specification) => {
        const sectionName = specification.section || '';
        let section = sections.find((item) => item.name === sectionName);
        if (!section) {
            section = { name: sectionName, items: [], subsections: [] };
            sections.push(section);
        }
        section.items.push(specification);
        const subsectionName = specification.subsection || '';
        let subsection = section.subsections.find((item) => item.name === subsectionName);
        if (!subsection) {
            subsection = { name: subsectionName, items: [] };
            section.subsections.push(subsection);
        }
        subsection.items.push(specification);
    });
    const sortOtherLast = (a: { name: string }, b: { name: string }) => {
        if (a.name === otherSectionName && b.name !== otherSectionName) {
            return 1;
        }
        if (b.name === otherSectionName && a.name !== otherSectionName) {
            return -1;
        }
        return 0;
    };
    sections.sort(sortOtherLast);
    sections.forEach((section) => section.subsections.sort(sortOtherLast));

    const activeTab = sectionTabs.find((tab) => tab.name === activeTabName);
    const activeSubtab = activeSubtabName ? activeTab?.subtabs.find((subtab) => subtab.name === activeSubtabName) : undefined;
    const selectedSection = (activeSubtabName ? activeSubtab?.activeSection : activeTab?.activeSection) || 'All';

    return (
        <>
            {componentsLoading &&
                <div className={`${componentName}__table-container`}>
                    <table className={`${componentName}__table`}>
                        <tbody>
                            {new Array(6).fill(0).map((_, index) => (
                                <tr key={index} className={`${componentName}__table-row ${componentName}__table-row-skeleton`}>
                                    <th className={`${componentName}__table-cell-skeleton`}>
                                        <div className={`${componentName}__table-cell-skeleton-content ${componentName}__loading-animation`}></div>
                                    </th>
                                    <td className={`${componentName}__table-cell-skeleton`}>
                                        <div className={`${componentName}__table-cell-skeleton-content ${componentName}__loading-animation`}></div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            }
            {!componentsLoading && !error && rows.length > 0 &&
                <>
                    {sections.length > 1 && (
                        <div className={`${componentName}__spec-filter`}>
                            <CustomSelect
                                componentName={componentName}
                                className={`${componentName}__spec-filter-select`}
                                ariaLabel={i18n('Filter by section')}
                                value={selectedSection}
                                options={[
                                    { value: 'All', label: i18n('All Sections') },
                                    ...sections.map((section) => ({
                                        value: section.name,
                                        label: section.name,
                                    })),
                                ]}
                                onChange={(value) => {
                                    setActiveSubsection(activeTabName, activeSubtabName || null, value);
                                    updateUrlHash();
                                }}
                                i18n={i18n}
                            />
                        </div>
                    )}
                    {sections.map((section) => (
                        (selectedSection === 'All' || selectedSection === section.name) && (
                            <div key={section.name || 'default'} className={`${componentName}__table-section`}>
                                {section.name && (sections.length > 1 || section.name !== otherSectionName) && (
                                    <div className={`${componentName}__table-section-header`}>
                                        <span className={`${componentName}__table-section-name`}>{section.name}</span>
                                        {section.items.some((item) => item.cas) && <span className={`${componentName}__table-section-tag`}>{section.items.find((item) => item.cas)?.cas}</span>}
                                    </div>
                                )}
                                <div className={`${componentName}__table-section-content`}>
                                    {section.subsections.map((subsection) => (
                                        <div key={subsection.name || 'default'} className={`${componentName}__table-section-group`}>
                                            {subsection.name && (section.subsections.length > 1 || subsection.name !== otherSectionName) && (
                                                <div className={`${componentName}__table-section-title`}>
                                                    {subsection.name}
                                                </div>
                                            )}
                                            <table className={`${componentName}__table`}>
                                                <tbody>
                                                    {subsection.items.map((item, index) => (
                                                        <tr key={`${item.name}-${index}`} className={`${componentName}__table-row`}>
                                                            <th className={`${componentName}__table-cell`}>{item.name}</th>
                                                            <td className={`${componentName}__table-cell`} dangerouslySetInnerHTML={{ __html: item.value }}></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )
                    ))}
                </>
            }
        </>
    );
}

export default GenericSpecificationSection;
